import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'

const PlayerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
`

const PlayButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.disabled ? '#e0e0e0' : '#f6f6f6'};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover {
    background: ${props => props.disabled ? '#e0e0e0' : '#eaeaea'};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ProgressBar = styled.div`
  flex-grow: 1;
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  position: relative;
`

const Progress = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #3249d7;
  border-radius: 2px;
  width: ${props => props.width}%;
`

const TimeDisplay = styled.span`
  font-size: 14px;
  color: #6c757d;
  min-width: 50px;
`

const HiddenAudio = styled.audio`
  display: none;
`

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 5v14l11-7z" fill="#3249D7" />
  </svg>
)

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" fill="#3249D7" />
  </svg>
)

const AudioPlayer = ({ 
  url, 
  onAudioComplete, 
  isAlreadyCompleted,
  initialPosition = 0,
  onTimeUpdate
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState(initialPosition)
  const [duration, setDuration] = useState(0)
  const [isCompleted, setIsCompleted] = useState(isAlreadyCompleted || false)
  const [audioError, setAudioError] = useState(null)
  const audioRef = useRef(null)
  const hasSetInitialPosition = useRef(false)

  // Update isCompleted if the prop changes
  useEffect(() => {
    if (isAlreadyCompleted) {
      setIsCompleted(true);
    }
  }, [isAlreadyCompleted]);
  
  useEffect(() => {
    // Reset states when URL changes
    setProgress(0)
    setAudioError(null)
    
    // Don't reset isCompleted here, as it should persist
    
    if (!url) {
      console.warn("No audio URL provided");
      return;
    }
  }, [url]);

  // Set initial position when audio is loaded
  useEffect(() => {
    if (audioRef.current && initialPosition > 0 && !hasSetInitialPosition.current) {
      const setPosition = () => {
        if (audioRef.current.readyState >= 2) {
          audioRef.current.currentTime = initialPosition;
          setCurrentTime(initialPosition);
          hasSetInitialPosition.current = true;
        } else {
          setTimeout(setPosition, 100);
        }
      };
      
      setPosition();
    }
  }, [initialPosition]);

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const audioDuration = audioRef.current.duration;
      setDuration(audioDuration);
      
      // Try setting initial position again after metadata is loaded
      if (initialPosition > 0 && !hasSetInitialPosition.current) {
        audioRef.current.currentTime = initialPosition;
        setCurrentTime(initialPosition);
        hasSetInitialPosition.current = true;
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const audioCurrentTime = audioRef.current.currentTime;
      const audioDuration = audioRef.current.duration || 0;
      setCurrentTime(audioCurrentTime);
      
      if (audioDuration > 0) {
        const progressValue = (audioCurrentTime / audioDuration) * 100;
        setProgress(progressValue);
      }
      
      // Report time back to parent component
      if (onTimeUpdate && typeof onTimeUpdate === 'function') {
        onTimeUpdate(audioCurrentTime);
      }
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(100);
    setIsCompleted(true);
    
    if (onAudioComplete && typeof onAudioComplete === 'function') {
      onAudioComplete();
    }
  };

  const handleError = (e) => {
    console.error("Audio error:", e);
    const errorMessage = e.target.error ? `Error: ${e.target.error.code} - ${e.target.error.message}` : 'Unknown error';
    console.error(errorMessage);
    setAudioError(`Error loading audio: ${errorMessage}`);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (!audioRef.current || isCompleted) return;
  
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setAudioError(null);
      
      try {
        const playPromise = audioRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch(error => {
              console.error("Audio playback failed:", error);
              setAudioError(`Playback failed: ${error.message}`);
              setIsPlaying(false);
              alert("Audio playback failed. Please check if your browser allows audio playback or try clicking again.");
            });
        } else {
          console.log("Play promise is undefined, setting isPlaying directly");
          setIsPlaying(true);
        }
      } catch (e) {
        console.error("Exception during play:", e);
        setAudioError(`Exception: ${e.message}`);
      }
    }
  };

  const formatTime = seconds => {
    if (isNaN(seconds) || seconds < 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <HiddenAudio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onError={handleError}
      />
      
      <PlayerContainer>
        <PlayButton 
          onClick={togglePlay}
          disabled={isCompleted || !url}
          type="button"
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </PlayButton>
        <ProgressBar>
          <Progress width={progress} />
        </ProgressBar>
        <TimeDisplay>
          {formatTime(currentTime)}/{formatTime(duration)}
        </TimeDisplay>
      </PlayerContainer>
      
      {audioError && (
        <div style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
          {audioError}
        </div>
      )}
      
      {isCompleted && (
        <div style={{ color: '#777', fontSize: '14px', marginTop: '5px' }}>
          You've completed this audio.
        </div>
      )}
    </>
  );
};

export default AudioPlayer;